"use client";

import type { FC } from "react";
import type { CodeSnippet } from "./OmniboxDeveloper";
import type { Key } from "react-aria-components";

import { useTranslations } from "next-intl";
import { useCallback, useMemo } from "react";

import { ListBox } from "@ui/components/core/ListBox";
import { clsx } from "clsx";
import { memo } from "react";
import { Button, SelectProps, SelectValue, Select as RACSelect } from "react-aria-components";

const Select = memo(function Select<T extends { id: string; textValue: string }>({
  items,
  ...props
}: Omit<SelectProps<T>, "children"> & {
  items: Array<T>;
}) {
  return (
    <RACSelect {...props}>
      <Button
        className={({ isFocusVisible }) =>
          clsx(
            "tw-text-sm/none tw-font-medium tw-text-black tw-flex tw-items-center tw-gap-2 tw-h-9 tw-pl-3 tw-pr-2 tw-rounded-full tw-bg-white hover:tw-bg-gray-50 tw-transition-colors",
            isFocusVisible ? "tw-outline-focus" : "tw-outline-none",
          )
        }
        style={{
          boxShadow: "0 0 1px 0 rgba(0, 0, 0, 0.40), 0 1px 1px 0 rgba(0, 0, 0, 0.04), 0 2px 4px 0 rgba(0, 0, 0, 0.04)",
        }}
      >
        <SelectValue />
        <svg viewBox='0 0 24 24' fill='none' className='tw-size-4 tw-text-gray-400 tw-flex-none' aria-hidden>
          <path
            d='M8 10L11.2929 13.2929C11.6834 13.6834 12.3166 13.6834 12.7071 13.2929L16 10'
            stroke='currentColor'
            strokeWidth='2.25'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
      </Button>
      <ListBox
        items={items}
        style={{ minWidth: "calc(max(11rem, var(--trigger-width) + 0.5rem) + 2rem)" }}
        offset={8}
        crossOffset={-4}
      />
    </RACSelect>
  );
});

interface LanguageSelectProps {
  activeIndex: number;
  onSelect: (index: number) => void;
  snippets: CodeSnippet[];
  onOpenChange?: (open: boolean) => void;
}

export const LanguageSelect: FC<LanguageSelectProps> = ({ activeIndex, onSelect, snippets, onOpenChange }) => {
  const t = useTranslations("ClientKeys.Globals");
  const items = useMemo(
    () =>
      snippets.map((snippet, idx) => ({
        id: snippet.language,
        textValue: snippet.label,
        index: idx,
      })),
    [snippets],
  );

  const activeItem = items[activeIndex];

  const handleSelectionChange = useCallback(
    (key: Key | null) => {
      if (key === null) return;
      const idx = items.findIndex(item => item.id === key);
      if (idx !== -1) {
        onSelect(idx);
      }
    },
    [items, onSelect],
  );

  return (
    <Select
      items={items}
      selectedKey={activeItem?.id}
      onSelectionChange={handleSelectionChange}
      onOpenChange={onOpenChange}
      aria-label={t("select_language")}
    />
  );
};
